import React, { useState } from 'react';

import './LOFAScheduleFacilityRow.scss';
import {
    GetLofaSchedule_getLofaSchedule as LofaScheduleSummaryData,
    GetLofaSchedule_getLofaSchedule_jobSummaries as LoadFacilityDetailData,
    GetAllLofas_getAllLofas as Lofa,
    CountLoadsByJobSummaryIdsAndStatus,
    CountLoadsByJobSummeryIdsAndStatuses,
} from '../../../../types/schemaTypes';
import { useStoreContext } from '../../../App/StoreProvider';
import * as LOFAScheduleEditActions from '../../../../store/actions/lofaScheduleEdit';
import { Textfield } from '../../../_common/Textfield/Textfield';
import { InputTitleEnum } from '../../../_common/InputTitleEnum/InputTitleEnum';
import { useMutation } from '@apollo/client/react';
import { COUNT_LOADS_BY_JOBSUMMARY_IDS_AND_STATUS } from '../../../../graphql/client/queries';
import { COUNT_LOADS_BY_JOBSUMMARY_IDS_AND_STATUSES } from '../../../../graphql/client/mutations';
import { THREE_NUMBERS_MASK, SPECIAL_CHARACTERS_MASK, SPECIAL_CHARACTERS_MASK_WITH_UNDERSCORE } from '../../../../utils/helpers/maskHelper';
import { EditActions } from '../../../../components/_common/EditLoadErrors/EditLoadErrors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import SearchableDropdownV2, { DropdownOption } from '../../../_common/SearchableDropdown/SearchableDropdownV2';

library.add(fas);

interface LOFAScheduleFacilityRowProps {
    lofaDetail: LofaScheduleSummaryData;
    facility: LoadFacilityDetailData;
    lofas: (Lofa | null)[] | null;
    isDeleteEnabled: boolean;
    totalVolume: number | null;
}

const DUPLICATE_FACILITY_VALUES_ERROR_MESSAGE =
    'Facility with the same LOFA Name, SandPO and TransportationPO values already exists.';
const LOFA_VOLUME_ERROR_MESSAGE = 'The LOFA Volume cannot exceed the Proppant Volume.';
const LOFA_VOLUME_ZERO_ERROR = 'The LOFA Volume cannot be less than 0';

const LofaScheduleFacilityRow = ({
    lofaDetail,
    facility,
    lofas,
    isDeleteEnabled,
    totalVolume,
}: LOFAScheduleFacilityRowProps): React.ReactElement => {
    const [state, dispatch] = useStoreContext();
    const { lofaScheduleEdit } = state;
    const [duplicateFacilityValuesError, setDuplicateFacilityValuesError] = useState('');
    const [lofaVolumeError, setLofaVolumeError] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [maxVolBoxErrorMessage, setMaxVolBoxErrorMessage] = useState('');
    const [facilityTotalVolume] = useState(totalVolume);

    const updateLofaScheduleFacilityValue = (eventKey: string | null): void => {
        let errorMessage = '';
        const lofaName =
            lofas &&
            lofas
                .filter(item => eventKey != null && item && item.lofaId === +eventKey)
                .map(item => item && item.lofaName)[0];
        const existingLOFAItem = lofaScheduleEdit.toBePosted.filter(
            item => item.proppantId === lofaDetail.proppantId,
        )[0];

        if (existingLOFAItem) {
            const currentEditingFacility = existingLOFAItem.jobSummaries.filter(item => item.id === facility.id)[0];

            if (lofaDetail && lofaDetail.jobSummaries && lofaDetail.jobSummaries.length !== 0) {
                lofaDetail.jobSummaries.forEach((item: any) => {
                    if (item.id !== facility.id) {
                        if (
                            item.sandPo === currentEditingFacility.sandPo &&
                            item.transportationPo === currentEditingFacility.transportationPo &&
                            item.lofaName === lofaName
                        ) {
                            errorMessage = DUPLICATE_FACILITY_VALUES_ERROR_MESSAGE;
                            setDuplicateFacilityValuesError(errorMessage);
                            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(true));
                        } else {
                            setDuplicateFacilityValuesError('');
                            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
                        }
                    }
                });
            }
        }

        if (errorMessage === '') {
            const lofaValue = {
                id: (eventKey && +eventKey) || 0,
                value: lofaName,
            };

            dispatch(LOFAScheduleEditActions.updateFacilityValue(lofaDetail, facility, lofaValue));
        }
    };

    const updateFacilitySandPOValue = (value: string | number | null | undefined): void => {
        let errorMessage = '';
        const existingLOFAItem = lofaScheduleEdit.toBePosted.filter(
            (item: any) => item.proppantId === lofaDetail.proppantId,
        )[0];

        if (existingLOFAItem) {
            const currentEditingFacility = existingLOFAItem.jobSummaries.filter(
                (item: any) => item.id === facility.id,
            )[0];

            if (lofaDetail && lofaDetail.jobSummaries && lofaDetail.jobSummaries.length !== 0) {
                lofaDetail.jobSummaries.forEach((item: any) => {
                    if (item.id !== facility.id) {
                        if (
                            item.sandPo === value &&
                            item.transportationPo === currentEditingFacility.transportationPo &&
                            item.lofaName === currentEditingFacility.lofaName
                        ) {
                            errorMessage = DUPLICATE_FACILITY_VALUES_ERROR_MESSAGE;
                            setDuplicateFacilityValuesError(errorMessage);
                            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(true));
                        } else {
                            setDuplicateFacilityValuesError('');
                            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
                        }
                    }
                });
            }
        }

        if (errorMessage === '') {
            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
            dispatch(LOFAScheduleEditActions.updateFacility(lofaDetail, facility, InputTitleEnum.SAND_PO, value));
        }
    };

    const updateFacilityTransportationPOValue = (value: string | number | null | undefined): void => {
        let errorMessage = '';
        const existingLOFAItem = lofaScheduleEdit.toBePosted.filter(
            (item: any) => item.proppantId === lofaDetail.proppantId,
        )[0];
        if (existingLOFAItem) {
            const currentEditingFacility = existingLOFAItem.jobSummaries.filter(
                (item: any) => item.id === facility.id,
            )[0];

            if (lofaDetail && lofaDetail.jobSummaries && lofaDetail.jobSummaries.length !== 0) {
                lofaDetail.jobSummaries.forEach((item: any) => {
                    if (item.id !== facility.id) {
                        if (
                            item.sandPo === currentEditingFacility.sandPo &&
                            item.transportationPo === value &&
                            item.lofaName === currentEditingFacility.lofaName
                        ) {
                            errorMessage = DUPLICATE_FACILITY_VALUES_ERROR_MESSAGE;
                            setDuplicateFacilityValuesError(errorMessage);
                            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(true));
                        } else {
                            setDuplicateFacilityValuesError('');
                            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
                        }
                    }
                });
            }
        }

        if (errorMessage === '') {
            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
            dispatch(
                LOFAScheduleEditActions.updateFacility(lofaDetail, facility, InputTitleEnum.TRANSPORTATION_PO, value),
            );
        }
    };

    const updateLofaVolumeValue = (value: any): void => {
        let allFacilitiesTotalVolume = 0;
        if (lofaScheduleEdit.toBePosted.length > 0) {
            const existingLOFAItem = lofaScheduleEdit.toBePosted.filter(
                (item: any) => item.proppantId === lofaDetail.proppantId,
            )[0];

            existingLOFAItem &&
                existingLOFAItem.jobSummaries &&
                existingLOFAItem.jobSummaries.forEach((item: any) => {
                    if (item.id !== facility.id && item.isDeleted !== true && item.lofaVolume != undefined) {
                        allFacilitiesTotalVolume += +item.lofaVolume;
                    }
                });
        } else {
            const existingLOFAItem = lofaScheduleEdit.currentValues.filter(
                (item: any) => item.proppantId === lofaDetail.proppantId,
            )[0];
            existingLOFAItem &&
                existingLOFAItem.jobSummaries &&
                existingLOFAItem.jobSummaries.forEach((item: any) => {
                    if (item.id !== facility.id) {
                        allFacilitiesTotalVolume += +item.lofaVolume;
                    }
                });
        }
        if (value && facility && facilityTotalVolume) {
            if (+value + allFacilitiesTotalVolume <= facilityTotalVolume && +value > 0) {
                if (lofaVolumeError !== '') {
                    setLofaVolumeError('');
                }
                dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
                dispatch(
                    LOFAScheduleEditActions.updateFacility(lofaDetail, facility, InputTitleEnum.LOFA_VOLUME, value),
                );
            } else if (+value < 1) {
                setLofaVolumeError(LOFA_VOLUME_ZERO_ERROR);
            } else if (!isNaN(+value)) {
                dispatch(LOFAScheduleEditActions.updateFacility(lofaDetail, facility, InputTitleEnum.LOFA_VOLUME, 0));
                setLofaVolumeError(LOFA_VOLUME_ERROR_MESSAGE);
            }
        }
        if (value === '') {
            dispatch(LOFAScheduleEditActions.updateFacility(lofaDetail, facility, InputTitleEnum.LOFA_VOLUME, 0));
            setLofaVolumeError(LOFA_VOLUME_ZERO_ERROR);
        }
    };

    const [CallCountApi] = useMutation<CountLoadsByJobSummeryIdsAndStatuses>(
        COUNT_LOADS_BY_JOBSUMMARY_IDS_AND_STATUSES,
        {
            onCompleted: data => {
                debugger;

                if (
                    data &&
                    data.countLoadsByJobSummeryIdsAndStatuses &&
                    data.countLoadsByJobSummeryIdsAndStatuses.count !== 0
                ) {
                    setErrorMessage('LOFA has existing Loads');
                    dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(true));
                } else {
                    dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
                    dispatch(LOFAScheduleEditActions.deleteFacility(lofaDetail, facility));
                }
                
            },
            onError: (e: Error) => console.log('Error', e),
        },
    );

    const deleteFacility = (): void => {
        debugger;
        CallCountApi({ variables: { input: { jobSummaryIds: [facility.jobSummaryId], statuses: [] } } });
    };

    const mapLofasToDropdownOptions: (DropdownOption | null)[] = lofas
        ? lofas.map((lofa: Lofa | null): DropdownOption | null => {
              return lofa && lofa.lofaId && lofa.lofaName
                  ? {
                        id: lofa.lofaId,
                        name: lofa.lofaName,
                    }
                  : null;
          })
        : [];

    const validateAndUpdateMaxVolBox = (value: any): void => {
        if (value && +value > 999 && +value < 56001) {
            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
            setMaxVolBoxErrorMessage('');
            dispatch(
                LOFAScheduleEditActions.updateFacility(lofaDetail, facility, InputTitleEnum.VOLUME_PER_BOX, value),
            );
        } else {
            dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(true));
            setMaxVolBoxErrorMessage(EditActions.MAX_VOL_BOX_ERROR);
        }
    };

    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    return (
        <div className="LOFAScheduleEdit-Container">
            <div className="LOFAScheduleEdit-EditDetailContainer">
                <Textfield
                    label="VOLUME"
                    disabled
                    value={facilityTotalVolume}
                    inputTitle={InputTitleEnum.VOLUME}
                    thousandSeparator
                />
            </div>
            {isDeleteEnabled && (
                <div className="LOFAScheduleEdit-DeleteIcon">
                    <i className="material-icons LOFAScheduleEdit-DeleteIcon" onClick={deleteFacility}>
                        highlight_off
                    </i>
                </div>
            )}
            <div className="LOFAScheduleEdit-EditDetailContainer LOFAScheduleEdit-LoadingFacilityContainer">
                <SearchableDropdownV2
                    autoCompleteEnabled={true}
                    dropdownOptions={mapLofasToDropdownOptions}
                    errorMessage={errorMessage != '' ? errorMessage : duplicateFacilityValuesError}
                    hasError={duplicateFacilityValuesError !== '' || errorMessage !== ''}
                    id="loadingFacility"
                    label="LOADING FACILITY"
                    onSelect={(eventKey: string | null) => {
                        updateLofaScheduleFacilityValue(eventKey);
                    }}
                    value={facility.lofaName}
                    endIcon={searchIcon}
                />
            </div>
            <div className="LOFAScheduleEdit-EditDetailContainer">
                <Textfield
                    label="LOFA VOLUME"
                    onChange={(value): void => {
                        updateLofaVolumeValue(value);
                    }}
                    value={facility.lofaVolume}
                    inputTitle={InputTitleEnum.LOFA_VOLUME}
                    error={errorMessage !== '' || lofaVolumeError !== ''}
                    helperText={errorMessage === '' ? (lofaVolumeError !== '' ? lofaVolumeError : '') : ''}
                    thousandSeparator
                    maxLength={11}
                />
            </div>
            <div className="LOFAScheduleEdit-EditDetailContainer">
                <Textfield
                    label="MILES FROM WELL"
                    mask={THREE_NUMBERS_MASK}
                    onChange={(event): void => {
                        dispatch(LOFAScheduleEditActions.setFormEnabledDisabled(false));
                        dispatch(
                            LOFAScheduleEditActions.updateFacility(
                                lofaDetail,
                                facility,
                                InputTitleEnum.MILES_FROM_WELL,
                                event.target.value,
                            ),
                        );
                    }}
                    value={facility.milesFromWell}
                    inputTitle={InputTitleEnum.MILES_FROM_WELL}
                    error={errorMessage !== ''}
                />
            </div>
            <div className="LOFAScheduleEdit-EditDetailContainer">
                <Textfield
                    label="MAX VOL/BOX"
                    onChange={(value): void => {
                        validateAndUpdateMaxVolBox(value);
                    }}
                    value={facility.volumePerBox}
                    inputTitle={InputTitleEnum.VOLUME_PER_BOX}
                    error={errorMessage !== '' || maxVolBoxErrorMessage !== ''}
                    helperText={errorMessage || maxVolBoxErrorMessage}
                    thousandSeparator
                    maxLength={6}
                />
            </div>
            <div className="LOFAScheduleEdit-EditDetailContainer">
                <Textfield
                    label="SAND PO"
                    onChange={(event): void => {
                        updateFacilitySandPOValue(event.target.value);
                    }}
                    value={facility.sandPo}
                    inputTitle={InputTitleEnum.SAND_PO}
                    error={duplicateFacilityValuesError !== '' || errorMessage !== ''}
                    helperText={
                        errorMessage === ''
                            ? duplicateFacilityValuesError !== ''
                                ? duplicateFacilityValuesError
                                : ''
                            : ''
                    }
                    mask={SPECIAL_CHARACTERS_MASK_WITH_UNDERSCORE}
                    allow_underscore={true}
                />
            </div>
            <div className="LOFAScheduleEdit-EditDetailContainer">
                <Textfield
                    label="TRANSPORTATION PO"
                    onChange={(event): void => {
                        updateFacilityTransportationPOValue(event.target.value);
                    }}
                    value={facility.transportationPo}
                    inputTitle={InputTitleEnum.TRANSPORTATION_PO}
                    error={duplicateFacilityValuesError !== '' || errorMessage !== ''}
                    helperText={
                        errorMessage === ''
                            ? duplicateFacilityValuesError !== ''
                                ? duplicateFacilityValuesError
                                : ''
                            : ''
                    }
                    mask={SPECIAL_CHARACTERS_MASK}
                />
            </div>
        </div>
    );
};

export default LofaScheduleFacilityRow;
